import { useCallback, useEffect, useState } from 'react';
import Field from '../components/Field';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import { ErrorBanner, InfoBanner, Toast } from '../components/Feedback';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';

const KINDS = [
  { key: 'TEACHER', label: 'Teachers' },
  { key: 'STUDENT_BOOK', label: 'Student books' },
];

const STATUSES = [
  { key: 'ALL', label: 'Everything' },
  { key: 'RENEWAL_DUE', label: 'Renewal due' },
  { key: 'EXPIRED', label: 'Expired' },
];

const PAGE_SIZE = 20;

/**
 * Module 11's Renewal Report: accounts nearing or past expiry, with the action on them.
 *
 * <p>Two tabs rather than one merged list, because teacher windows and student books
 * live in different tables — merging them would mean paging in memory, and a page drawn
 * from a list the database never counted shows a total that answers a different
 * question. Each tab carries its own real count.
 *
 * <p>Sorted soonest-first and not re-sortable: this is a worklist, and its order is the
 * order the work has to be done in.
 */
export default function RenewalsPage() {
  const { user } = useAuth();
  const [kind, setKind] = useState('TEACHER');
  const [status, setStatus] = useState('ALL');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);
  const [note, setNote] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rows, counts, recent] = await Promise.all([
        api.renewals({ kind, status, q: search || undefined, page, size: PAGE_SIZE }),
        api.renewalSummary(),
        api.renewalHistory(8),
      ]);
      setData(rows);
      setSummary(counts);
      setHistory(recent);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [kind, status, search, page]);

  useEffect(() => {
    load();
  }, [load]);

  async function approve() {
    setWorking(true);
    setError(null);
    try {
      const result = await api.approveRenewal(approving.id, { kind: approving.kind, note: note || undefined });
      setApproving(null);
      setNote('');
      setToast(`${result.personName} renewed to ${new Date(result.newExpiresAt).toLocaleDateString()}`);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setWorking(false);
    }
  }

  const rows = data?.items ?? [];
  const dueCount = kind === 'TEACHER' ? summary?.teachersRenewalDue : summary?.booksRenewalDue;
  const expiredCount = kind === 'TEACHER' ? summary?.teachersExpired : summary?.booksExpired;

  return (
    <>
      <div className="page-head">
        <h1>Renewals</h1>
        <p>
          Access runs for 15 months. This is everyone inside the last{' '}
          {summary?.firstAlertDays ?? 30} days of it, and everyone already past it.
        </p>
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      {summary?.scoped && (
        <InfoBanner>
          These are the schools in the states assigned to you. Platform-wide renewals are the Super
          Admin&apos;s.
        </InfoBanner>
      )}

      {summary && (
        <div className="kpi-row">
          <Kpi label="Teachers · renewal due" value={summary.teachersRenewalDue}
               note={`${summary.teachersExpired.toLocaleString()} already expired`} />
          <Kpi label="Student books · renewal due" value={summary.booksRenewalDue}
               note={`${summary.booksExpired.toLocaleString()} already expired`} />
          <Kpi label="Needing attention now"
               value={summary.teachersExpired + summary.booksExpired}
               note="Cannot open anything until renewed" />
        </div>
      )}

      <div className="filter-chips" style={{ marginBottom: 'var(--s1)' }} role="tablist">
        {KINDS.map((entry) => (
          <button
            type="button"
            key={entry.key}
            role="tab"
            className="chip"
            aria-pressed={kind === entry.key}
            onClick={() => { setKind(entry.key); setPage(0); }}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <section className="card card-pad" style={{ marginBottom: 'var(--s1)' }}>
        <div className="toolbar">
          {STATUSES.map((entry) => (
            <button
              type="button"
              key={entry.key}
              className="chip"
              aria-pressed={status === entry.key}
              onClick={() => { setStatus(entry.key); setPage(0); }}
            >
              {entry.label}
            </button>
          ))}
          <input
            className="input"
            style={{ flex: '1 1 220px' }}
            placeholder={kind === 'TEACHER' ? 'Search a teacher or mobile' : 'Search a student, mobile or book'}
            aria-label="Search"
            maxLength={100}
            defaultValue={search}
            onKeyDown={(event) => {
              if (event.key === 'Enter') { setSearch(event.currentTarget.value.trim()); setPage(0); }
            }}
            onBlur={(event) => { setSearch(event.target.value.trim()); setPage(0); }}
          />
        </div>
        <p className="tiny muted" style={{ margin: '10px 0 0' }}>
          {dueCount === undefined
            ? ''
            : `${dueCount.toLocaleString()} due for renewal · ${expiredCount.toLocaleString()} expired`}
        </p>
      </section>

      <section className="card">
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>{kind === 'TEACHER' ? 'Teacher' : 'Student'}</th>
                {kind === 'STUDENT_BOOK' && <th>Book</th>}
                <th>School</th>
                <th>Expires</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    {row.personName}
                    <div className="tiny muted mono">{row.mobile}</div>
                  </td>
                  {kind === 'STUDENT_BOOK' && <td>{row.titleName}</td>}
                  <td>{row.schoolName ?? <span className="muted">Not linked</span>}</td>
                  <td className="mono">
                    {new Date(row.expiresAt).toLocaleDateString()}
                    <div className="tiny muted">
                      {row.status === 'SUSPENDED'
                        ? 'Access closed'
                        : `${row.daysRemaining} day${row.daysRemaining === 1 ? '' : 's'} left`}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${row.status === 'SUSPENDED' ? 'badge-danger' : 'badge-warn'}`}>
                      {row.statusLabel}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button type="button" className="btn btn-secondary btn-sm"
                            onClick={() => { setApproving(row); setNote(''); }}>
                      Renew 15 months
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && <div className="empty">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="empty">Nothing is due for renewal here.</div>
        )}

        {data && data.totalPages > 1 && (
          <div className="row-between" style={{ padding: 'var(--s1)' }}>
            <span className="tiny muted">
              Page {data.page + 1} of {data.totalPages} · {data.totalItems.toLocaleString()} rows
            </span>
            <div className="toolbar">
              <button type="button" className="btn btn-secondary btn-sm" disabled={data.page === 0}
                      onClick={() => setPage((current) => Math.max(current - 1, 0))}>
                Previous
              </button>
              <button type="button" className="btn btn-secondary btn-sm" disabled={!data.hasNext}
                      onClick={() => setPage((current) => current + 1)}>
                Next
              </button>
            </div>
          </div>
        )}
      </section>

      {history.length > 0 && (
        <section className="card card-pad panel" style={{ marginTop: 'var(--s2)' }}>
          <h2>Recently renewed</h2>
          <p className="panel-sub">Every approval is recorded with who made it.</p>
          <ul className="timeline">
            {history.map((entry, index) => (
              <li key={`${entry.approvedAt}-${index}`}>
                <span className="dot-mark" />
                <div className="timeline-body">
                  <b>{entry.personName}</b>
                  {entry.titleName ? ` · ${entry.titleName}` : ''}
                  <p>
                    Renewed to {new Date(entry.newExpiresAt).toLocaleDateString()} by{' '}
                    {entry.approvedByName} on {new Date(entry.approvedAt).toLocaleDateString()}
                    {entry.note ? ` — ${entry.note}` : ''}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="tiny muted" style={{ marginTop: 'var(--s2)' }}>
        Renewing adds 15 months to whichever is later, the current expiry or today — so an
        early renewal keeps the remaining term, and a lapsed account gets a full one.
        {user?.role === 'SALES_REP' ? ' You can renew accounts at your own schools.' : ''}
      </p>

      {approving && (
        <Modal
          title="Renew this access?"
          width={520}
          onClose={() => setApproving(null)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setApproving(null)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" disabled={working} onClick={approve}>
                {working ? 'Renewing…' : 'Approve renewal'}
                <Icon name="check" size={15} />
              </button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            <strong>{approving.personName}</strong>
            {approving.titleName ? ` · ${approving.titleName}` : ''} currently expires{' '}
            {new Date(approving.expiresAt).toLocaleDateString()}.
          </p>
          <Field label="Note (optional)" id="renewal-note"
                 hint="Recorded with the renewal — a purchase order, a conversation, anything worth keeping.">
            <input
              id="renewal-note"
              className="input"
              maxLength={500}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </Field>
        </Modal>
      )}

      <Toast message={toast} onDone={() => setTimeout(() => setToast(null), 2400)} />
    </>
  );
}

function Kpi({ label, value, note }) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value.toLocaleString()}</div>
      {note && <div className="kpi-note">{note}</div>}
    </div>
  );
}
